import React, { useCallback } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ChecklistItemRow from './ChecklistItemRow';
import ChecklistDismissButton from './ChecklistDismissButton';
import { useChecklist, runChecklistAction, trackChecklistEvent } from '@/constants/checklist';

const isWeb = Platform.OS === 'web';

// ─── Daily claim row (post-checklist) ────────────────────────

function DailyClaimRow({ navigation }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const {
    dailyLoading, dailyAvailable, dailyClaiming, dailyJustClaimed, claimDaily, shouldShowDaily,
  } = useChecklist();

  const handlePress = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_drawer_clicked');
    const ok = await claimDaily();
    if (ok && navigation) {
      setTimeout(() => navigation.closeDrawer(), 800);
    }
  }, [dailyAvailable, dailyClaiming, claimDaily, navigation]);

  if (!shouldShowDaily) return null;
  if (!dailyLoading && !dailyAvailable && !dailyJustClaimed) return null;

  const height = 44;
  const busy = dailyLoading || dailyClaiming;

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={dailyJustClaimed ? 'Daily credits claimed' : 'Claim daily credits'}
      disabled={busy || dailyJustClaimed}
      style={({ hovered, pressed }) => [
        {
          minHeight: height,
          paddingVertical: 8,
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 10,
          borderRadius: isWeb ? 6 : 8,
          gap: isWeb ? 8 : 10,
        },
        isWeb && hovered && !dailyJustClaimed && !busy && { backgroundColor: colors.solid_color_tertiary },
        !isWeb && pressed && !dailyJustClaimed && { opacity: 0.7 },
        busy && { opacity: 0.6 },
      ]}
    >
      <View style={{ width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
        <Icon
          name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
          size={isWeb ? 14 : 16}
          color={colors.text_color_secondary}
        />
      </View>

      <Text
        numberOfLines={1}
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: '400',
          color: dailyJustClaimed ? colors.text_color_secondary : colors.text_color_primary,
          lineHeight: isXS ? 22 : 20,
        }}
      >
        {dailyJustClaimed ? 'Claimed!' : 'Daily reward'}
      </Text>
    </Pressable>
  );
}

// ─── Checklist section (pre-completion) ──────────────────────

function ChecklistSection({ navigation }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const {
    completionMap, completedCount, sortedItems, total,
    dailyAvailable, dailyClaiming, dailyJustClaimed, claimDaily,
    showCredits, collapsed, toggleCollapsed, dismiss,
  } = useChecklist();

  const handleItemPress = useCallback((item) => {
    trackChecklistEvent('item_clicked', item.id);
    if (navigation) navigation.closeDrawer();
    if (item.action) {
      runChecklistAction(item.action, { router, openModal, isXS });
      return;
    }
    if (item.route) {
      router.push(item.route);
    }
  }, [router, navigation, openModal, isXS]);

  const handleEarnMore = useCallback(() => {
    trackChecklistEvent('earn_more_clicked');
    if (navigation) navigation.closeDrawer();
    if (isWeb) {
      openModal('SettingsModalWeb', { tab: 'earn' });
    } else {
      openModal('SettingsModalMobile', { initialScreen: 'earn' });
    }
  }, [openModal, navigation]);

  const handleDailyClaim = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_drawer_clicked');
    const ok = await claimDaily();
    if (ok && navigation) {
      setTimeout(() => navigation.closeDrawer(), 800);
    }
  }, [dailyAvailable, dailyClaiming, claimDaily, navigation]);

  const pct = Math.round((completedCount / total) * 100);
  const headerHeight = isWeb ? (isXS ? 40 : 32) : 44;
  const showDailyRow = showCredits && (dailyAvailable || dailyJustClaimed);

  return (
    <View>
      <View style={styles.header}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Get Started"
          accessibilityState={{ expanded: !collapsed }}
          aria-expanded={!collapsed}
          onPress={() => {
            toggleCollapsed();
            if (collapsed) trackChecklistEvent('expanded_drawer');
          }}
          style={({ hovered, pressed }) => [
            {
              minHeight: headerHeight,
              flex: 1,
              minWidth: 0,
              paddingVertical: 8,
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: isWeb ? 10 : 14,
              paddingRight: 10,
              borderRadius: isWeb ? 6 : 8,
              gap: isWeb ? 8 : 10,
            },
            isWeb && hovered && { backgroundColor: colors.solid_color_tertiary },
            !isWeb && pressed && { opacity: 0.7 },
          ]}
        >
          <View style={{ width: 18, height: 18, alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="clipboard-check" size={16} color={colors.text_color_secondary} />
          </View>

          <Text
            numberOfLines={1}
            style={{
              flex: 1,
              fontSize: 13,
              fontWeight: '500',
              color: colors.text_color_primary,
              lineHeight: isXS ? 22 : 20,
              paddingLeft: isWeb ? 0 : 4,
            }}
          >
            Get Started
          </Text>

          <View style={styles.headerRight}>
            <Text style={[styles.count, { color: colors.text_color_tertiary }]}>
              {completedCount}/{total}
            </Text>
            <Icon
              name={collapsed ? 'chevron-right' : 'chevron-down'}
              size={isWeb ? 12 : 14}
              color={colors.text_color_tertiary}
            />
          </View>
        </Pressable>
        <ChecklistDismissButton onPress={dismiss} colors={colors} />
      </View>

      {!collapsed && (
        <View style={styles.summary}>
          <Text style={[styles.summaryText, { color: colors.text_color_tertiary }]}>
            Discover what you can do.
          </Text>
          <View
            accessibilityRole="progressbar"
            accessibilityLabel="Getting started progress"
            accessibilityValue={{ min: 0, max: total, now: completedCount }}
            style={[styles.progressTrack, { backgroundColor: colors.border_color_secondary }]}
          >
            <View style={[styles.progressFill, {
              width: `${pct}%`,
              backgroundColor: colors.text_color_secondary,
            }]} />
          </View>
        </View>
      )}

      {/* Expanded items */}
      {!collapsed && (
        <>
          {/* Daily claim row */}
          {showDailyRow && (
            <Pressable
              onPress={handleDailyClaim}
              accessibilityRole="button"
              accessibilityLabel={dailyJustClaimed ? 'Daily credits claimed' : 'Claim daily credits'}
              disabled={dailyClaiming || dailyJustClaimed}
              style={({ hovered, pressed }) => [
                {
                  minHeight: 44,
                  paddingVertical: 8,
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: 10,
                  borderRadius: isWeb ? 6 : 8,
                  gap: isWeb ? 8 : 10,
                },
                isWeb && hovered && !dailyJustClaimed && { backgroundColor: colors.solid_color_tertiary },
                !isWeb && pressed && !dailyJustClaimed && { opacity: 0.7 },
                dailyClaiming && { opacity: 0.6 },
              ]}
            >
              <View style={{ width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
                <Icon
                  name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
                  size={isWeb ? 14 : 16}
                  color={colors.text_color_secondary}
                />
              </View>
              <Text
                numberOfLines={1}
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: '400',
                  color: dailyJustClaimed ? colors.text_color_secondary : colors.text_color_primary,
                  lineHeight: isXS ? 22 : 20,
                }}
              >
                {dailyJustClaimed ? 'Claimed!' : 'Daily credits'}
              </Text>
            </Pressable>
          )}

          {sortedItems.map((item) => (
            <ChecklistItemRow
              key={item.id}
              item={item}
              completed={completionMap[item.id]}
              onPress={() => handleItemPress(item)}
              colors={colors}
            />
          ))}

          {showCredits && (
            <Pressable
              onPress={handleEarnMore}
              accessibilityRole="button"
              accessibilityLabel="View all rewards"
              style={({ hovered, pressed }) => [
                styles.rewardsLink,
                (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
              ]}
            >
              <Text style={[styles.rewardsLabel, { color: colors.text_color_secondary }]}>
                View all rewards
              </Text>
              <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
            </Pressable>
          )}
        </>
      )}
    </View>
  );
}

// ─── Router: picks the right mode ────────────────────────────

function NativeDrawerChecklist({ navigation }) {
  const { shouldShow, shouldShowDaily, isComplete } = useChecklist();

  if (shouldShow) return <ChecklistSection navigation={navigation} />;
  if (shouldShowDaily && isComplete) return <DailyClaimRow navigation={navigation} />;
  return null;
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center' },
  summary: {
    paddingHorizontal: 10,
    paddingTop: 4,
    paddingBottom: 8,
    gap: 12,
  },
  summaryText: { fontSize: 11, lineHeight: 16 },
  count: { fontSize: 11, lineHeight: 16, fontVariant: ['tabular-nums'] },
  rewardsLink: {
    minHeight: isWeb ? 36 : 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 4,
    marginBottom: 8,
    borderRadius: 8,
    gap: 8,
  },
  rewardsLabel: { fontSize: 12, lineHeight: 18, fontWeight: '500', flexShrink: 1 },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  progressTrack: {
    height: 2,
    borderRadius: 1,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 1,
  },
});

export default React.memo(NativeDrawerChecklist);
